import { Checkbox, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { ButtonStyled } from './SubCategoriesFilter.styled.';
import { useTickets } from 'contexts/TicketsContext';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { TicketRequestSubcategoryI } from 'declarations/interfaces/ticketRequestSubcategories';
import { useFetchTicketRequestSubcategories } from 'hooks/api/useTicketSubcategories';

type SubCategoriesFilterT = {
  shouldFiltersReset: number;
};

export const SubCategoriesFilter: React.FC<SubCategoriesFilterT> = ({
  shouldFiltersReset,
}): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [checked, setChecked] = useState<number[]>([]);
  const { updateSearchParamsArray, getAllSearchParam } = useTickets();

  const ticketSubCategoriesResult = useFetchTicketRequestSubcategories({
    isEnabled: true,
    roofTopId: undefined,
  });
  const [items, setItems] = useState<TicketRequestSubcategoryI[]>([]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleToggle = (categoryId: number) => (): void => {
    setChecked((prevChecked): number[] => {
      const currentIndex = prevChecked.indexOf(categoryId);
      const newChecked = [...prevChecked];

      if (currentIndex === -1) {
        newChecked.push(categoryId);
      } else {
        newChecked.splice(currentIndex, 1);
      }

      updateSearchParamsArray(
        TicketsRefinementKeysE.SubCategory,
        newChecked.length > 0 ? newChecked : null
      );

      return newChecked;
    });
  };

  const handleSelectAll = (): void => {
    setChecked((prevChecked): number[] => {
      if (prevChecked.length === items.length) {
        updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
        return [];
      } else {
        const allIds = items.map((item): number => item.id);
        updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, allIds);
        return allIds;
      }
    });
  };

  const getButtonLabel = (): string => {
    if (checked.length === items.length) {
      return 'All Subcategories';
    } else if (checked.length > 0) {
      return items
        .filter((item): boolean => checked.includes(item.id))
        .map((item): string => item.name)
        .join(', ');
    } else {
      return 'Select Subcategory';
    }
  };

  const buttonLabel = getButtonLabel();

  useEffect(() => {
    setChecked([]);
  }, [shouldFiltersReset]);

  useEffect(() => {
    if (ticketSubCategoriesResult.data) {
      setItems(ticketSubCategoriesResult.data);
    }
  }, [ticketSubCategoriesResult.data]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const subCategoryIdsString = searchParams.get(TicketsRefinementKeysE.SubCategory) || '';
    const subCategoryIds = subCategoryIdsString
      .split(',')
      .filter(id => id) // Remove empty strings
      .map(id => Number(id))
      .filter(id => !isNaN(id));

    setChecked(subCategoryIds);
  }, [getAllSearchParam]);

  if (ticketSubCategoriesResult.isLoading) {
    return (
      <ButtonStyled variant="outlined" disabled>
        Subcategories
      </ButtonStyled>
    );
  }

  if (ticketSubCategoriesResult.error) {
    return <div>Error loading subcategories</div>;
  }

  return (
    <div>
      <Tooltip title={buttonLabel} placement="top">
        <ButtonStyled
          aria-controls="checkbox-list-menu"
          aria-haspopup="true"
          variant="outlined"
          onClick={handleClick}
          style={{
            maxWidth: '140px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            justifyContent: 'start',
          }}
        >
          {buttonLabel}
        </ButtonStyled>
      </Tooltip>
      <Menu
        id="checkbox-list-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleSelectAll}>
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={checked.length === items.length}
              indeterminate={checked.length > 0 && checked.length < items.length}
              tabIndex={-1}
              disableRipple
            />
          </ListItemIcon>
          <ListItemText primary="Select All" />
        </MenuItem>
        {items.map(
          (subCategory): JSX.Element => (
            <MenuItem key={subCategory.id} onClick={handleToggle(subCategory.id)}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={checked.includes(subCategory.id)}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={subCategory.name} />
            </MenuItem>
          )
        )}
      </Menu>
    </div>
  );
};
